import { useState } from "react";
import type { UiVotingPhase, VotingSession } from "@/lib/clubs/voting";
import styles from "./VotingStateMachine.module.scss";

const PHASES: { id: UiVotingPhase; label: string; sub: string }[] = [
  { id: "SETUP", label: "Setup", sub: "Configure session" },
  { id: "NOMINATING", label: "Nominations", sub: "Members add books" },
  { id: "RANKING", label: "Ranking", sub: "Members rank finalists" },
  { id: "CLOSED", label: "Result", sub: "Winner announced" },
];

const PHASE_INDEX: Record<UiVotingPhase, number> = {
  SETUP: 0,
  NOMINATING: 1,
  RANKING: 2,
  CLOSED: 3,
};

interface Props {
  clubName: string;
  phase: UiVotingPhase;
  session: VotingSession | null;
  isStarting: boolean;
  isAdvancing: boolean;
  isDeleting: boolean;
  error: Error | null;
  canAdvance: boolean;
  onStartSession: () => void;
  onAdvancePhase: () => void;
  onDeleteSession: () => void;
}

const ADVANCE_LABEL: Record<UiVotingPhase, string> = {
  SETUP: "Open nominations →",
  NOMINATING: "Advance to ranking →",
  RANKING: "End ranking →",
  CLOSED: "",
};

/**
 * Pure state-machine component for the voting cockpit. Renders the correct
 * controls per phase. Does no data fetching — all data and callbacks are
 * supplied by the parent route.
 */
export function VotingStateMachine({
  clubName,
  phase,
  session,
  isStarting,
  isAdvancing,
  isDeleting,
  error,
  canAdvance,
  onStartSession,
  onAdvancePhase,
  onDeleteSession,
}: Props) {
  const [tab, setTab] = useState<UiVotingPhase>(phase);
  const currentIdx = PHASE_INDEX[phase];

  const advanceLabel = ADVANCE_LABEL[phase];
  const showAdvance = phase === "NOMINATING" || phase === "RANKING";
  const showOpenNominations = phase === "SETUP" && session !== null;
  const showStart = phase === "SETUP" && session === null;
  const showDelete = session !== null && phase !== "CLOSED";

  return (
    <div className={styles.shell}>
      <header className={styles.head}>
        <div className={styles.headText}>
          <p className={styles.eyebrow}>{clubName} · Voting session</p>
          <h1 className={styles.title}>{titleFor(phase)}</h1>
          <p className={styles.sub}>{subtitleFor(phase, session)}</p>
        </div>
        <div className={styles.headBtns}>
          {showDelete && (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnDanger}`}
              onClick={onDeleteSession}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting…" : "Delete session"}
            </button>
          )}
          {showStart && (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={onStartSession}
              disabled={isStarting}
            >
              {isStarting ? "Starting…" : "Start voting session"}
            </button>
          )}
          {showOpenNominations && (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={onAdvancePhase}
              disabled={isAdvancing || !canAdvance}
            >
              {isAdvancing ? "Opening…" : "Open nominations →"}
            </button>
          )}
          {showAdvance && (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={onAdvancePhase}
              disabled={isAdvancing || !canAdvance}
            >
              {isAdvancing ? "Working…" : advanceLabel}
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className={styles.errorBanner} role="alert">
          {errorMessage(error)}
        </div>
      )}

      <nav className={styles.tabs} role="tablist">
        {PHASES.map((p) => {
          const idx = PHASE_INDEX[p.id];
          const state =
            idx < currentIdx ? "done" : idx === currentIdx ? "active" : "todo";
          return (
            <button
              key={p.id}
              role="tab"
              type="button"
              aria-selected={tab === p.id}
              className={`${styles.tab} ${tab === p.id ? styles.tabSelected : ""} ${styles[`tab_${state}`]}`}
              onClick={() => setTab(p.id)}
            >
              <span className={styles.tabPos}>0{idx + 1}</span>
              <span className={styles.tabLabel}>{p.label}</span>
              <span className={styles.tabSub}>{p.sub}</span>
            </button>
          );
        })}
      </nav>

      <section className={styles.body}>
        {tab === "SETUP" && <SetupPane phase={phase} session={session} />}
        {tab === "NOMINATING" && <NominationsPane session={session} />}
        {tab === "RANKING" && <RankingPane session={session} />}
        {tab === "CLOSED" && <ClosedPane phase={phase} session={session} />}
      </section>
    </div>
  );
}

function SetupPane({
  phase,
  session,
}: {
  phase: UiVotingPhase;
  session: VotingSession | null;
}) {
  if (phase !== "SETUP") {
    return (
      <div>
        <h2 className={styles.paneTitle}>Setup · session locked</h2>
        <p className={styles.paneText}>
          Settings can&rsquo;t be edited once nominations begin.
        </p>
      </div>
    );
  }
  if (!session) {
    return (
      <div>
        <h2 className={styles.paneTitle}>No active voting session</h2>
        <p className={styles.paneText}>
          Start a session to let members nominate books for your next read.
        </p>
      </div>
    );
  }
  return (
    <div>
      <h2 className={styles.paneTitle}>Session created · ready to open</h2>
      <p className={styles.paneText}>
        Open nominations when you&rsquo;re ready for members to add books.
      </p>
      <p className={styles.paneText}>
        Eligible voters: {session.memberCount} members.
      </p>
    </div>
  );
}

function NominationsPane({ session }: { session: VotingSession | null }) {
  if (!session || session.nominations.length === 0) {
    return (
      <div>
        <h2 className={styles.paneTitle}>Nominations</h2>
        <p className={styles.empty}>No nominations yet.</p>
      </div>
    );
  }
  return (
    <div>
      <h2 className={styles.paneTitle}>Nominations</h2>
      <p className={styles.paneText}>
        {session.nominations.length} books nominated so far.
      </p>
      <ul className={styles.list}>
        {session.nominations.map((n) => (
          <li key={n.id} className={styles.row}>
            <span className={styles.rowTitle}>{n.book.title}</span>
            <span className={styles.rowMeta}>
              {n.book.author ?? "Unknown author"} · nominated by{" "}
              {n.user.displayName ?? "a member"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RankingPane({ session }: { session: VotingSession | null }) {
  if (!session) {
    return (
      <div>
        <h2 className={styles.paneTitle}>Live tally</h2>
        <p className={styles.empty}>No session.</p>
      </div>
    );
  }
  if (session.nominations.length === 0) {
    return (
      <div>
        <h2 className={styles.paneTitle}>Live tally</h2>
        <p className={styles.empty}>No finalists.</p>
      </div>
    );
  }
  const max = Math.max(session.rankingCount, 1);
  return (
    <div>
      <h2 className={styles.paneTitle}>Live tally</h2>
      <p className={styles.paneText}>
        {session.rankingCount} of {session.memberCount} ballots in
      </p>
      <ol className={styles.rankList}>
        {session.nominations.map((n, i) => (
          <li key={n.id} className={styles.rankRow}>
            <span className={styles.rankRowPos}>{i + 1}</span>
            <span>
              <strong className={styles.rowTitle}>{n.book.title}</strong>
              <br />
              <span className={styles.rowMeta}>
                {n.book.author ?? "Unknown author"}
              </span>
            </span>
            <span className={styles.rankBar}>
              <span
                className={styles.rankBarFill}
                style={{ width: `${(session.rankingCount / max) * 100}%` }}
              />
            </span>
            <span className={styles.rankPts}>—</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ClosedPane({
  phase,
  session,
}: {
  phase: UiVotingPhase;
  session: VotingSession | null;
}) {
  if (phase !== "CLOSED" || !session) {
    return (
      <div>
        <p className={styles.empty}>
          The winner appears here once ranking closes.
        </p>
      </div>
    );
  }
  const winner = session.winnerBook;
  if (!winner) {
    return (
      <div>
        <p className={styles.empty}>No winner recorded.</p>
      </div>
    );
  }
  return (
    <div>
      <div className={styles.winnerCard}>
        {winner.coverUrl ? (
          <img
            className={styles.winnerCover}
            src={winner.coverUrl}
            alt=""
            aria-hidden="true"
          />
        ) : (
          <div className={styles.winnerCover} aria-hidden="true" />
        )}
        <div className={styles.winnerBody}>
          <p className={styles.winnerEyebrow}>Winner</p>
          <h3 className={styles.winnerTitle}>{winner.title}</h3>
          {winner.author && (
            <p className={styles.winnerAuthor}>{winner.author}</p>
          )}
        </div>
      </div>
      <p className={styles.paneText}>
        Use this book to start a new reading room.
      </p>
    </div>
  );
}

function titleFor(phase: UiVotingPhase): string {
  if (phase === "SETUP") return "Voting · setup";
  if (phase === "NOMINATING") return "Voting · nominations";
  if (phase === "RANKING") return "Voting · ranking";
  return "Voting · result";
}

function subtitleFor(phase: UiVotingPhase, session: VotingSession | null) {
  if (!session) return "No session yet.";
  if (phase === "NOMINATING") {
    return `${session.nominationCount} nominations · ${session.memberCount} members`;
  }
  if (phase === "RANKING") {
    return `${session.rankingCount} of ${session.memberCount} ballots in`;
  }
  if (phase === "CLOSED") {
    return "Winner announced.";
  }
  return "Configure your session.";
}

function errorMessage(error: Error): string {
  return error.message || "Something went wrong.";
}
